import { createAsyncThunk, createSlice} from "@reduxjs/toolkit"
import axios from "axios"
import { defaultSocketService } from "../context/socket.context" 
import { confirmWorkspaceTransfer } from "./manageWorkspaceSlice"
import {resetWorkspaceSearches} from "./workspaceSearchSlice"
import {selectUser} from "./userSlice"
import {getImportedArticleCountForWorkspace} from "./freeTierTrackerslice"
import {fetchArticleWorkspaces} from "./recentlyReviewedSlice"
import {fetchTopLevelNotes} from "./workspaceNoteSlice"
import {getAutomatedSearchList} from "./automatedSearchViewSlice"

/**
 * a small utility function that takes UserWorkspace entities, and translates them into proper Workspace entities that our app can consume.
 *
 * @param userWorkspaceVal {UserWorkspaceDTO}
 * @returns {FrontendUserWorkspace}
 */
export const normalizeUserWorkspace = (userWorkspaceVal) => {
	const permissions = {read: userWorkspaceVal.read, write: userWorkspaceVal.write, admin: userWorkspaceVal.admin}
	const ownerIsFreeTier = userWorkspaceVal.ownerIsFreeTier
	const ownerIsSuperadmin = userWorkspaceVal.ownerIsSuperadmin
	const {id, title, description, ownerUserId} = userWorkspaceVal.workspace

	return {
		id,
		title,
		description,
		ownerUserId,
		permissions,
		ownerIsFreeTier,
		ownerIsSuperadmin,
	}
}

export const leaveCurrentWorkspace = createAsyncThunk(
	"workspace/leaveCurrentWorkspace",
	(workspaceId, {dispatch, getState}) => {
		const user = selectUser(getState())
		defaultSocketService.emit("LEAVE_WORKSPACE", user.sub, workspaceId)
		dispatch(resetWorkspaceSearches(workspaceId))
	}
)

export const leaveSecondaryWorkspace = createAsyncThunk(
	"workspace/leaveSecondaryWorkspace",
	(_, { dispatch, getState }) => {
		const secondaryWorkspaceId = selectSecondaryWorkspaceId(getState())
		dispatch(leaveCurrentWorkspace(secondaryWorkspaceId))
	}
)

export const loadWorkspace = createAsyncThunk(
	"workspace/loadWorkspace",
	async ( workspace, {dispatch, rejectWithValue}) => {
		try {

			if (workspace.ownerIsFreeTier) {
				await dispatch(getImportedArticleCountForWorkspace({fullWorkspace: workspace}))
			}
			const {data} = await axios.get(`/api/workspace/${workspace.id}/automatedSearch/exclusions`)
			await dispatch(fetchTopLevelNotes(workspace.id))
			await dispatch(fetchArticleWorkspaces(workspace.id))
			dispatch(getAutomatedSearchList({workspaceId: workspace.id}))
			return {
				workspace,
				exclusions: data
			}
		} catch (err) {
			console.error("exclusions didn't make it: ", err)
			return rejectWithValue(err)
		}

	}
)

export const openPrimaryWorkspace = createAsyncThunk(
	"workspace/openPrimaryWorkspace",
	async (workspace, {dispatch, getState, rejectWithValue}) => {
		try {
			const currentPrimaryWorkspace = selectWorkspace(getState())

			if(!!currentPrimaryWorkspace && currentPrimaryWorkspace.id !== workspace.id) {
				dispatch(leaveCurrentWorkspace(currentPrimaryWorkspace.id))
			}
			const {payload} = await dispatch(loadWorkspace(workspace))
			defaultSocketService
				.timeout(5000)
				.emit("ENTER_WORKSPACE", workspace.id, (response) => {
					if(response && response.error) {
						console.error("ENTER_WORKSPACE ack error for Primary Workspace:", response.error)
						return rejectWithValue(response.error.message)
					}
				})
			return payload
		} catch (err) {
			console.error("exclusions didn't make it: ", err)
			return rejectWithValue(err)
		}
	}
)

export const openSecondaryWorkspace = createAsyncThunk(
	"workspace/openSecondaryWorkspace",
	async (workspace, {dispatch, getState, rejectWithValue}) => {
		try {
			const currentSecondaryWorkspace = selectSecondaryWorkspace(getState())

			if(!!currentSecondaryWorkspace && currentSecondaryWorkspace.id !== workspace.id) {
				dispatch(leaveCurrentWorkspace(currentSecondaryWorkspace.id))
			}
			const {payload} = await dispatch(loadWorkspace(workspace))
			defaultSocketService.emit("ENTER_WORKSPACE", workspace.id, (response) => {
				if(response.error) {
					console.error("ENTER_WORKSPACE ack error for Secondary Workspace:", response.error)
					return rejectWithValue(response.error.message)
				}
			})
			return payload
		} catch (err) {
			console.error("exclusions didn't make it: ", err)
			return rejectWithValue(err)
		}
	}
)

export const createNewWorkspace = createAsyncThunk(
	"workspace/createNewWorkspace",
	async ({title, description}, {dispatch, rejectWithValue}) => {
		try {
			const {data} = await axios.post("/api/workspace", { title, description })
			const {newWorkspace, userWorkspaces} = data
			const newUserWorkspace = userWorkspaces.find(userWorkspace => userWorkspace.workspaceId === newWorkspace.id)
			const newWorkspaceObject = {
				...newWorkspace,
				permissions: {read: true, write: true, admin: true}, // user created the workspace means user has full permissions
				ownerIsFreeTier: newUserWorkspace.ownerIsFreeTier,
			}

			dispatch(openPrimaryWorkspace(newWorkspaceObject))
		} catch(error) {
			console.error("ERROR CREATING NEW WORKSPACE: ", error)
			rejectWithValue(error)

		}

	}
)

export const workspaceSlice = createSlice({
	name: "workspace",
	initialState: {
		byWorkspace: {},
		primaryId: null,
		secondaryId: null,
	},
	reducers: {
		setPrimaryWorkspace: (state, action) => {
			const workspace = action.payload
			const primaryId = workspace.id
			state.primaryId = primaryId
			state.byWorkspace[primaryId] = workspace
		},
		setSecondaryWorkspace: (state, action) => {
			const workspace = action.payload
			const secondaryId = workspace.id
			state.secondaryId = secondaryId
			state.byWorkspace[secondaryId] = workspace
		},
		clearSecondaryWorkspace: (state) => {
			state.secondaryId = null
		},
		resetWorkspace: (state, action) => {
			delete state.byWorkspace[action.payload]
			state.primaryId = null
		},
		setWorkspaceExclusions: (state, action) => {
			const {workspace, exclusions} = action.payload
			state.byWorkspace[workspace.id]["excludedArticles"] = {}
			exclusions.map((PMID) => {
				state.byWorkspace[workspace.id].excludedArticles[PMID] = PMID
			})
		}
	},
	extraReducers: (builder) => {
		builder
			.addCase(
				confirmWorkspaceTransfer.fulfilled,
				(state, action) => {
					const {
						ownerIsFreeTier,
						ownerUserId,
						id: workspaceId
					} = action.payload
					state.byWorkspace[workspaceId].ownerIsFreeTier = ownerIsFreeTier
					state.byWorkspace[workspaceId].ownerUserId = parseInt(ownerUserId)
				}
			)
			.addCase(
				openPrimaryWorkspace.fulfilled,
				(state, action) => {
					const {workspace} = action.payload
					workspaceSlice.caseReducers.setPrimaryWorkspace(state, {payload: workspace})
					workspaceSlice.caseReducers.setWorkspaceExclusions(state, action)
				}
			)
			.addCase(
				openSecondaryWorkspace.fulfilled,
				(state, action) => {
					const {workspace} = action.payload
					workspaceSlice.caseReducers.setSecondaryWorkspace(state, {payload: workspace})
					workspaceSlice.caseReducers.setWorkspaceExclusions(state, action)
				}
			)
			.addCase(
				leaveSecondaryWorkspace.fulfilled,
				workspaceSlice.caseReducers.clearSecondaryWorkspace
			)
	}
})

export const {
	setWorkspaceExclusions,
	resetWorkspace,
	clearSecondaryWorkspace,
} = workspaceSlice.actions

const defaultPermissions = {read: false, write: false, admin: false}

export const selectWorkspace = (state) => state.workspace.byWorkspace?.[state.workspace.primaryId] ?? null
export const selectSecondaryWorkspace = (state) => state.workspace.byWorkspace[state.workspace?.secondaryId]

export const selectWorkspaceExclusions = (state, workspaceId) =>
	state.workspace.byWorkspace[workspaceId]?.excludedArticles ?? []
export const selectWorkspacePermissions = (state, workspaceId) => state.workspace.byWorkspace[workspaceId]?.permissions ?? defaultPermissions
export const selectShowSecondaryWorkspace = (state) => state.workspace?.secondaryId !== null
export const selectPrimaryWorkspaceId = (state) => state.workspace?.primaryId
export const selectSecondaryWorkspaceId = (state) => state.workspace?.secondaryId

//TODO: really look at this function and compare it to how we handle our notes in the `noteRestrictionsMiddleware`
export const updateWorkspaceExclusions =
	(workspaceId, emit) => async (dispatch) => {
		//loosely modeled after the 'refresh relay' pattern in refreshRelaySlice.js
		if (emit === "emit") {
			defaultSocketService.emit("UPDATE_WORKSPACE_EXCLUSIONS", workspaceId)
		}
		const { data } = await axios.get(
			`/api/workspace/${workspaceId}/automatedSearch/exclusions`
		)
		dispatch(setWorkspaceExclusions({workspace:{id: workspaceId}, exclusions: data}))
	}

export default workspaceSlice.reducer
